import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import ErrorModal from "./ErrorModal";

const ScannerModal = ({ closeModal, getCode }) => {
  const [enviroment, setEnvironment] = useState("user");
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 786) {
        setEnvironment("environment");
      } else {
        setEnvironment("user");
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Every format the underlying barcode detector supports (1D + 2D)
  const barcodeFormats = [
    // 1D / linear
    "code_128",
    "code_39",
    "code_93",
    "codabar",
    "ean_13",
    "ean_8",
    "upc_a",
    "upc_e",
    "itf",
    // 2D
    "qr_code",
    "data_matrix",
    "pdf417",
    "aztec",
  ];

  // Ask for a high-resolution stream so long/dense barcodes (e.g. ICCID Code 128)
  // have enough pixels per bar to be decoded.
  const cameraConstraints = {
    facingMode: enviroment,
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  };

  const handleScan = (result) => {
    if (!result || result.length === 0) return;
    const value = (result[0].rawValue || "").trim();
    handleSubmit(value);
  };

  const handleSubmit = (data) => {
    if (data) {
      getCode(data);
      closeModal();
    }
  };

  const handleError = (error) => {
    console.error("Barcode Scanner Error:", error);
    setErrorModal({ open: true, message: `Scanner Error: ${error?.message || error}` });
  };

  return ReactDOM.createPortal(
    <div
      id="authentication-modal"
      tabIndex="-1"
      aria-hidden="false"
      className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50"
    >
      <div className="relative p-4 w-full max-w-lg max-h-full">
        {" "}
        {/* Expanded width */}
        <div className="relative bg-white rounded-lg shadow">
          <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
            <h3 className="text-xl font-semibold text-gray-900 ">
              Scan your Bar Code
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center "
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="p-4 md:p-5 flex flex-col items-center gap-3">
            <div
              id="scanner"
              style={{
                height: "300px",
                width: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {" "}
              <Scanner
                key={enviroment}
                formats={barcodeFormats}
                constraints={cameraConstraints}
                scanDelay={300}
                onScan={handleScan}
                onError={handleError}
                components={{
                  finder: false, // square finder makes users shrink wide 1D barcodes
                  torch: true,
                  zoom: true,
                }}
                styles={{
                  container: { width: "100%", height: "100%", aspectRatio: "auto" },
                  // "cover" fills the whole box on every device. Phones (esp. iPhone) send a
                  // portrait stream, which "contain" letterboxes into a narrow strip.
                  // Only the preview is cropped – decoding still uses the full camera frame.
                  video: { width: "100%", height: "100%", objectFit: "cover", background: "#000" },
                }}
              />
            </div>
            <p className="text-sm text-gray-500 text-center">
              Hold the barcode horizontally so it fills the width of the frame.
            </p>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {errorModal.open && (
        <ErrorModal
          message={errorModal.message}
          close={() => setErrorModal({ open: false, message: "" })}
        />
      )}
    </div>,
    document.body
  );
};

export default ScannerModal;
